import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@/shared/ui'

interface Props {
  isOpen: boolean
  title: string
  description?: string
  confirmLabel: string
  variant?: 'primary' | 'danger'
  /** Аннулирование счёта требует причину — показываем обязательное поле */
  requireReason?: boolean
  loading?: boolean
  onClose: () => void
  onSubmit: (reason: string) => void | Promise<void>
}

/** Модальное подтверждение действия: оплата, аннулирование, освобождение резерва. */
export const ConfirmActionModal: React.FC<Props> = ({
  isOpen,
  title,
  description,
  confirmLabel,
  variant = 'primary',
  requireReason = false,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (isOpen) setReason('')
  }, [isOpen])

  if (!isOpen) return null

  const trimmed = reason.trim()
  const reasonMissing = requireReason && !trimmed

  const handleSubmit = () => {
    if (reasonMissing) return
    void onSubmit(trimmed)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {description && <p className="text-sm text-gray-700">{description}</p>}

          {requireReason && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Причина <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={1000}
                rows={4}
                placeholder="Опишите причину..."
                className="w-full border border-gray-300 rounded-lg p-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              />
              <div className="text-right text-xs text-gray-400 mt-1">
                {reason.length}/1000
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            variant={variant}
            onClick={handleSubmit}
            isLoading={loading}
            disabled={reasonMissing}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
